import React from 'react';
import { Badge, Button, Group, Stack, Table, Text } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import type { BaselineRevisionSummary } from '../../../api/baselineApis';
import {
  baselineStatusColor,
  baselineStatusLabel,
  formatBaselineTimestamp,
} from '../logic/baselineHistoryUtils';

export interface BaselineRevisionTableProps {
  revisions: BaselineRevisionSummary[];
  selectedRevisionId: string | null;
  canDiscard: boolean;
  discarding: boolean;
  onSelect: (revision: BaselineRevisionSummary) => void;
  onDiscardRevision: (revision: BaselineRevisionSummary) => void;
}

export function BaselineRevisionTable({
  revisions,
  selectedRevisionId,
  canDiscard,
  discarding,
  onSelect,
  onDiscardRevision,
}: BaselineRevisionTableProps) {
  if (revisions.length === 0) {
    return (
      <Text size="sm" c="dimmed" p="md">
        No revisions for this slot.
      </Text>
    );
  }

  return (
    <Table.ScrollContainer minWidth={560}>
      <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Rev</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Reviewed</Table.Th>
            <Table.Th>Tasks</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {revisions.map((rev) => {
            const selected = rev.id === selectedRevisionId;
            const canDiscardRow =
              canDiscard && (rev.status === 'DRAFT' || rev.status === 'REJECTED');
            return (
              <Table.Tr
                key={rev.id}
                style={{
                  cursor: 'pointer',
                  background: selected
                    ? 'color-mix(in srgb, var(--mantine-color-brand-6) 8%, transparent)'
                    : undefined,
                }}
                onClick={() => onSelect(rev)}
              >
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>
                      #{rev.revisionNumber}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {rev.baselineName?.trim() || `Slot ${rev.baselineIndex}`}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={baselineStatusColor(rev.status)}
                    radius="sm"
                    style={{ textTransform: 'none' }}
                  >
                    {baselineStatusLabel(rev.status)}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{formatBaselineTimestamp(rev.createdAt)}</Text>
                  {rev.createdBy ? (
                    <Text size="xs" c="dimmed">
                      {rev.createdBy}
                    </Text>
                  ) : null}
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{formatBaselineTimestamp(rev.reviewedAt)}</Text>
                  {rev.reviewedBy ? (
                    <Text size="xs" c="dimmed">
                      {rev.reviewedBy}
                    </Text>
                  ) : null}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {rev.taskCount ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {canDiscardRow ? (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      leftSection={<IconTrash size={12} />}
                      loading={discarding}
                      disabled={discarding}
                      onClick={() => onDiscardRevision(rev)}
                    >
                      Discard
                    </Button>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
